import React, { useState, useEffect } from 'react';
import './App.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaExpandArrowsAlt, FaCompressArrowsAlt } from 'react-icons/fa';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

function App() {
  const [modalParam, setModalParam] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [responseMetadata, setResponseMetadata] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [humanSelection, setHumanSelection] = useState(null);
  const [humanSelections, setHumanSelections] = useState({});
  const [showSelectionPanel, setShowSelectionPanel] = useState(true);
  const [showImageModal, setShowImageModal] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    response: false,
    prompt: false,
    hyperparameters: false
  });
  const [annotatorMode, setAnnotatorMode] = useState(false);
  const [currentRun, setCurrentRun] = useState('Run1');
  const availableRuns = ['Run1', 'Run2'];

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Extract descriptive name from image paths
  const extractResponseName = (image1Path, image2Path) => {
    // Extract folder name (e.g., "JitterPlot" from "data\\images\\JitterPlot\\jitter_R1.png")
    const pathParts = image1Path.split(/[\\\/]/);
    const folderName = pathParts[pathParts.length - 2]; // Get folder before filename
    
    // Extract R numbers from both images
    const extractR = (path) => {
      const match = path.match(/[_-](R\d+)/i);
      return match ? match[1] : '';
    };
    
    const r1 = extractR(image1Path);
    const r2 = extractR(image2Path);
    
    return `${folderName} (${r1}, ${r2})`;
  };
  // Load list of available response files
  useEffect(() => {
    // Different file lists for each run
    const filesByRun = {
      'Run1': [
        'gpt-4.1_20251113_174855.json',
        'gpt-4.1_20251113_174908.json',
        'gpt-4.1_20251113_174924.json',
        'gpt-4.1_20251113_174937.json',
        'gpt-4.1_20251113_174955.json',
        'gpt-4.1_20251113_175014.json',
        'gpt-4.1_20251113_175029.json',
        'gpt-4.1_20251113_175042.json',
        'gpt-4.1_20251113_175056.json',
        'gpt-4.1_20251113_175112.json',
        'gpt-4.1_20251113_175128.json',
        'gpt-4.1_20251113_175143.json',
        'gpt-4.1_20251113_175203.json',
        'gpt-4.1_20251113_175217.json',
        'gpt-4.1_20251113_175230.json',
        'gpt-4.1_20251113_175243.json',
        'gpt-4.1_20251113_175259.json',
        'gpt-4.1_20251113_175315.json',
        'gpt-4.1_20251113_175329.json',
        'gpt-4.1_20251113_175348.json',
        'gpt-4.1_20251113_175403.json',
        'gpt-4.1_20251113_175418.json',
        'gpt-4.1_20251113_175429.json',
        'gpt-4.1_20251113_175440.json',
        'gpt-4.1_20251113_175452.json',
        'gpt-4.1_20251113_175505.json',
        'gpt-4.1_20251113_175522.json',
        'gpt-4.1_20251113_175539.json',
        'gpt-4.1_20251113_175608.json',
        'gpt-4.1_20251113_175643.json'
      ],
      'Run2': [
        'gpt-4.1_20251114_045702.json',
        'gpt-4.1_20251114_045718.json',
        'gpt-4.1_20251114_045729.json',
        'gpt-4.1_20251114_045743.json',
        'gpt-4.1_20251114_045755.json',
        'gpt-4.1_20251114_045805.json',
        'gpt-4.1_20251114_045819.json',
        'gpt-4.1_20251114_045831.json',
        'gpt-4.1_20251114_045852.json',
        'gpt-4.1_20251114_045904.json',
        'gpt-4.1_20251114_045915.json',
        'gpt-4.1_20251114_045927.json',
        'gpt-4.1_20251114_045938.json',
        'gpt-4.1_20251114_045951.json',
        'gpt-4.1_20251114_050001.json',
        'gpt-4.1_20251114_050016.json',
        'gpt-4.1_20251114_050029.json',
        'gpt-4.1_20251114_050044.json',
        'gpt-4.1_20251114_050055.json',
        'gpt-4.1_20251114_050111.json',
        'gpt-4.1_20251114_050124.json',
        'gpt-4.1_20251114_050135.json',
        'gpt-4.1_20251114_050202.json',
        'gpt-4.1_20251114_050231.json',
        'gpt-4.1_20251114_050245.json',
        'gpt-4.1_20251114_050259.json',
        'gpt-4.1_20251114_050314.json'
      ]
    };
    
    const files = filesByRun[currentRun] || [];
    
    // Reset state when switching runs
    setLoading(true);
    setResponseData(null);
    setError(null);
    setAvailableFiles(files);
    setCurrentFileIndex(0);
    
    // Load metadata for all files to get descriptive names
    Promise.all(
      files.map(fileName =>
        fetch(`/data/${currentRun}/responses/${fileName}`)
          .then(res => {
            if (!res.ok) {
              console.log("currentRun", currentRun)
              throw new Error(`HTTP error! status: ${res.status}`);
            }
            const contentType = res.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
              throw new Error("Not a JSON response");
            }
            return res.json();
          })
          .then(data => ({
            fileName,
            displayName: extractResponseName(data.input.image1_path, data.input.image2_path),
            timestamp: data.metadata.timestamp
          }))
          .catch(err => {
            console.error(`Error loading metadata for ${fileName}:`, err);
            return {
              fileName,
              displayName: fileName.replace('.json', ''),
              timestamp: ''
            };
          })
      )
    ).then(metadata => {
      setResponseMetadata(metadata);
    });
  }, [currentRun]);

  // Load JSON data from public folder
  useEffect(() => {
    // Wait for files to be loaded and ensure index is valid
    if (availableFiles.length === 0) return;
    if (currentFileIndex >= availableFiles.length) return;
    
    const fileName = availableFiles[currentFileIndex];
    if (!fileName) return;
    
    // Verify this file belongs to the current run
    const filesByRun = {
      'Run1': [
        'gpt-4.1_20251113_174855.json',
        'gpt-4.1_20251113_174908.json',
        'gpt-4.1_20251113_174924.json',
        'gpt-4.1_20251113_174937.json',
        'gpt-4.1_20251113_174955.json',
        'gpt-4.1_20251113_175014.json',
        'gpt-4.1_20251113_175029.json',
        'gpt-4.1_20251113_175042.json',
        'gpt-4.1_20251113_175056.json',
        'gpt-4.1_20251113_175112.json',
        'gpt-4.1_20251113_175128.json',
        'gpt-4.1_20251113_175143.json',
        'gpt-4.1_20251113_175203.json',
        'gpt-4.1_20251113_175217.json',
        'gpt-4.1_20251113_175230.json',
        'gpt-4.1_20251113_175243.json',
        'gpt-4.1_20251113_175259.json',
        'gpt-4.1_20251113_175315.json',
        'gpt-4.1_20251113_175329.json',
        'gpt-4.1_20251113_175348.json',
        'gpt-4.1_20251113_175403.json',
        'gpt-4.1_20251113_175418.json',
        'gpt-4.1_20251113_175429.json',
        'gpt-4.1_20251113_175440.json',
        'gpt-4.1_20251113_175452.json',
        'gpt-4.1_20251113_175505.json',
        'gpt-4.1_20251113_175522.json',
        'gpt-4.1_20251113_175539.json',
        'gpt-4.1_20251113_175608.json',
        'gpt-4.1_20251113_175643.json'
      ],
      'Run2': [
        'gpt-4.1_20251114_045702.json',
        'gpt-4.1_20251114_045718.json',
        'gpt-4.1_20251114_045729.json',
        'gpt-4.1_20251114_045743.json',
        'gpt-4.1_20251114_045755.json',
        'gpt-4.1_20251114_045805.json',
        'gpt-4.1_20251114_045819.json',
        'gpt-4.1_20251114_045831.json',
        'gpt-4.1_20251114_045852.json',
        'gpt-4.1_20251114_045904.json',
        'gpt-4.1_20251114_045915.json',
        'gpt-4.1_20251114_045927.json',
        'gpt-4.1_20251114_045938.json',
        'gpt-4.1_20251114_045951.json',
        'gpt-4.1_20251114_050001.json',
        'gpt-4.1_20251114_050016.json',
        'gpt-4.1_20251114_050029.json',
        'gpt-4.1_20251114_050044.json',
        'gpt-4.1_20251114_050055.json',
        'gpt-4.1_20251114_050111.json',
        'gpt-4.1_20251114_050124.json',
        'gpt-4.1_20251114_050135.json',
        'gpt-4.1_20251114_050202.json',
        'gpt-4.1_20251114_050231.json',
        'gpt-4.1_20251114_050245.json',
        'gpt-4.1_20251114_050259.json',
        'gpt-4.1_20251114_050314.json'
      ]
    };
    
    const currentRunFiles = filesByRun[currentRun] || [];
    if (!currentRunFiles.includes(fileName)) {
      console.log(`File ${fileName} not in ${currentRun}, skipping load`);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    fetch(`/data/${currentRun}/responses/${fileName}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load JSON file: ${response.status} ${response.statusText}`);
        }
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          throw new Error(`Expected JSON but got ${contentType}. The file might not exist.`);
        }
        return response.json();
      })
      .then(data => {
        setResponseData(data);
        setLoading(false);
        // Load saved selection for this file
        const selectionKey = `${currentRun}_${fileName}`;
        setHumanSelection(humanSelections[selectionKey] || null);
      })
      .catch(err => {
        console.error('Error loading response data:', err);
        setError(`Error loading ${fileName}: ${err.message}`);
        setLoading(false);
      });
  }, [currentFileIndex, currentRun, availableFiles.length, humanSelections]);

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app-container">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  if (!responseData) {
    return (
      <div className="app-container">
        <div className="loading-message">No data available</div>
      </div>
    );
  }

  // Navigation handlers
  const handlePrevious = () => {
    if (currentFileIndex > 0) {
      setCurrentFileIndex(currentFileIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentFileIndex < availableFiles.length - 1) {
      setCurrentFileIndex(currentFileIndex + 1);
    }
  };

  const handleFileSelect = (e) => {
    setCurrentFileIndex(parseInt(e.target.value));
  };

  const handleHumanSelection = (choice) => {
    const fileName = availableFiles[currentFileIndex];
    const selectionKey = `${currentRun}_${fileName}`;
    setHumanSelection(choice);
    setHumanSelections(prev => ({
      ...prev,
      [selectionKey]: choice
    }));
  };

  // Load data from JSON file
  const llmResponse = responseData.output.response;
  const prompt = responseData.input.prompt;
  // Normalize image paths from JSON (convert backslashes to forward slashes)
  const normalizePath = (p) => p ? p.replace(/\\/g, '/').replace(/^\/+/, '') : '';
  const image1Path = '/' + normalizePath(responseData.input.image1_path);
  const image2Path = '/' + normalizePath(responseData.input.image2_path);
  
  // Extract ranker IDs from image paths
  const extractRankerId = (path) => {
    const match = path.match(/[_-](R\d+)/i);
    return match ? match[1].toUpperCase() : '';
  };
  
  const ranker1Id = extractRankerId(responseData.input.image1_path);
  const ranker2Id = extractRankerId(responseData.input.image2_path);
  
  const hyperparameters = {
    temperature: {
      value: responseData.hyperparameters.temperature,
      title: "Temperature",
      description: "Controls randomness in the output. Lower values (0.1) make responses more focused and deterministic, while higher values (0.9) make them more creative and varied.",
      details: "Temperature is one of the most important hyperparameters for controlling the creativity of AI responses. When set to 0, the model will always choose the most likely next word, resulting in very predictable text. As you increase the temperature, the model becomes more willing to choose less likely words, leading to more creative but potentially less coherent responses."
    },
    tokensUsed: {
      value: responseData.hyperparameters.tokens_used,
      title: "Tokens Used",
      description: "Total number of tokens consumed in this request, including both input and output tokens.",
      details: "Tokens are the basic units that language models work with - they can be words, parts of words, or punctuation. This shows the actual token consumption for this specific request, which is important for cost tracking and API usage monitoring."
    },
    topP: {
      value: responseData.hyperparameters.top_p,
      title: "Top P (Nuclear Sampling)",
      description: "Nuclear sampling parameter. Considers only the top tokens whose cumulative probability is p. Lower values focus on more likely tokens.",
      details: "Top-p sampling is an alternative to temperature for controlling randomness. Instead of considering all possible next tokens, it only considers the smallest set of tokens whose cumulative probability exceeds the threshold p. For example, with top-p=0.9, only tokens that make up the top 90% probability mass are considered."
    },
    frequencyPenalty: {
      value: responseData.hyperparameters.frequency_penalty,
      title: "Frequency Penalty",
      description: "Reduces the likelihood of repeating tokens based on their frequency in the text. Higher values discourage repetition.",
      details: "This parameter penalizes tokens based on how frequently they appear in the generated text. A value of 0 means no penalty, while positive values increasingly discourage repetition. This is useful for reducing redundant or repetitive content in longer responses."
    },
    presencePenalty: {
      value: responseData.hyperparameters.presence_penalty,
      title: "Presence Penalty",
      description: "Reduces the likelihood of repeating any token that has already appeared. Encourages the model to talk about new topics.",
      details: "Unlike frequency penalty which considers how often a token appears, presence penalty simply checks whether a token has appeared before. It encourages the model to introduce new concepts and vocabulary, making responses more diverse and comprehensive."
    }
  };

  return (
    <div className="app-container">
      {/* Image Comparison Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal-content image-comparison-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Image Comparison</h3>
              <button className="modal-close" onClick={() => setShowImageModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body image-comparison-body">
              <div className="comparison-image-container">
                <h4 className="comparison-image-title">{ranker1Id}</h4>
                <img 
                  src={image1Path} 
                  alt="Comparison 1"
                  className="comparison-image"
                />
              </div>
              <div className="comparison-image-container">
                <h4 className="comparison-image-title">{ranker2Id}</h4>
                <img 
                  src={image2Path} 
                  alt="Comparison 2"
                  className="comparison-image"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <div className="navigation-bar">
        <button 
          className="nav-button"
          onClick={handlePrevious}
          disabled={currentFileIndex === 0}
          title="Previous response"
        >
          ‹ Previous
        </button>

        <FormControlLabel
          control={
            <Switch
              checked={annotatorMode}
              onChange={(e) => setAnnotatorMode(e.target.checked)}
              color="success"
            />
          }
          label="Annotator Mode"
          className="annotator-mode-switch"
        />

        <div className="run-selector-container">
          <label className="run-selector-label">Run:</label>
          <select 
            className="run-selector"
            value={currentRun}
            onChange={(e) => setCurrentRun(e.target.value)}
          >
            {availableRuns.map(run => (
              <option key={run} value={run}>{run}</option>
            ))}
          </select>
        </div>
        
        <div className="file-selector-container">
          <select 
            className="file-selector"
            value={currentFileIndex}
            onChange={handleFileSelect}
          >
            {(() => {
              // Group responses by visualization type
              const groups = {};
              availableFiles.forEach((file, index) => {
                const metadata = responseMetadata.find(m => m.fileName === file);
                const displayName = metadata ? metadata.displayName : file.replace('.json', '');
                // Extract visualization type (first part before parentheses)
                const vizType = displayName.split('(')[0].trim();
                if (!groups[vizType]) {
                  groups[vizType] = [];
                }
                groups[vizType].push({ index, displayName });
              });

              // Render grouped options
              return Object.keys(groups).sort().map(vizType => (
                <optgroup key={vizType} label={vizType}>
                  {groups[vizType].map(({ index, displayName }) => (
                    <option key={index} value={index}>
                      {displayName}
                    </option>
                  ))}
                </optgroup>
              ));
            })()}
          </select>
          <span className="file-counter">
            {currentFileIndex + 1} / {availableFiles.length}
          </span>
        </div>

        <button 
          className="nav-button"
          onClick={handleNext}
          disabled={currentFileIndex === availableFiles.length - 1}
          title="Next response"
        >
          Next ›
        </button>
      </div>

      {/* Parameter Info Modal */}
      {modalParam && (
        <div className="modal-overlay" onClick={() => setModalParam(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{hyperparameters[modalParam].title}</h3>
              <button className="modal-close" onClick={() => setModalParam(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                {hyperparameters[modalParam].description}
              </p>
              <div className="modal-details">
                <h4>Additional Details:</h4>
                <p>{hyperparameters[modalParam].details}</p>
              </div>
              <div className="modal-value">
                <strong>Current Value: </strong>
                <span className="param-value">{hyperparameters[modalParam].value}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Expand Modal */}
      {expandedSection && (
        <div className="modal-overlay" onClick={() => setExpandedSection(null)}>
          <div className="modal-content expanded-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {expandedSection === 'response' && 'LLM Response'}
                {expandedSection === 'prompt' && 'Prompt'}
                {expandedSection === 'hyperparameters' && 'Hyperparameters'}
              </h3>
              <button className="modal-close" onClick={() => setExpandedSection(null)}>
                ×
              </button>
            </div>
            <div className="modal-body expanded-modal-body">
              {expandedSection === 'response' && (
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{llmResponse}</ReactMarkdown>
                </div>
              )}
              {expandedSection === 'prompt' && (
                <div className="prompt-content">
                  <p>{prompt}</p>
                </div>
              )}
              {expandedSection === 'hyperparameters' && (
                <div className="hyperparameters-expanded">
                  {Object.entries(hyperparameters).map(([key, param]) => (
                    <div key={key} className="param-expanded-item">
                      <div className="param-expanded-header">
                        <span className="param-expanded-label">{param.title}:</span>
                        <span className="param-expanded-value">{param.value}</span>
                      </div>
                      <p className="param-expanded-description">{param.description}</p>
                      <div className="param-expanded-details">
                        <p>{param.details}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      {!annotatorMode ? (
        // Regular Mode
      <div className="content-area">
        {/* Left Panel */}
        <div className="left-panel">
          {/* Toggle Button */}
          <button 
            className="toggle-selection-panel"
            onClick={() => setShowSelectionPanel(!showSelectionPanel)}
            title={showSelectionPanel ? "Hide annotation panel" : "Show annotation panel"}
          >
            {showSelectionPanel ? '▼ Hide Human Annotation' : '▶ Show Human Annotation'}
          </button>

          {/* Human Selection */}
          {showSelectionPanel && (
            <div className="human-selection-panel">
              <h3 className="selection-title">Which scoring function is more stable?</h3>
              <div className="selection-buttons">
                <button
                  className={`selection-button first ${humanSelection === 'first' ? 'selected' : ''}`}
                  onClick={() => handleHumanSelection('first')}
                >
                  <span className="button-label">{ranker1Id}</span>
                  {humanSelection === 'first' && <span className="checkmark">✓</span>}
                </button>
                <button
                  className={`selection-button second ${humanSelection === 'second' ? 'selected' : ''}`}
                  onClick={() => handleHumanSelection('second')}
                >
                  <span className="button-label">{ranker2Id}</span>
                  {humanSelection === 'second' && <span className="checkmark">✓</span>}
                </button>
                <button
                  className={`selection-button unclear ${humanSelection === 'unclear' ? 'selected' : ''}`}
                  onClick={() => handleHumanSelection('unclear')}
                >
                  <span className="button-label">Unclear / Equal</span>
                  {humanSelection === 'unclear' && <span className="checkmark">✓</span>}
                </button>
              </div>
              {humanSelection && (
                <button 
                  className="clear-selection-button"
                  onClick={() => handleHumanSelection(null)}
                >
                  Clear Selection
                </button>
              )}
            </div>
          )}

          {/* LLM Response Section */}
        <div className={`section response-section ${collapsedSections.response ? 'collapsed' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">LLM Response</h2>
            <div className="section-header-buttons">
              <button 
                className="collapse-icon"
                onClick={() => toggleSection('response')}
                title={collapsedSections.response ? "Expand section" : "Collapse section"}
              >
                {collapsedSections.response ? <FaExpandArrowsAlt /> : <FaCompressArrowsAlt />}
              </button>
            </div>
          </div>
          {!collapsedSections.response && (
            <div className="section-content markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{llmResponse}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Prompt Section */}
        <div className={`section prompt-section ${collapsedSections.prompt ? 'collapsed' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Prompt</h2>
            <div className="section-header-buttons">
              <button 
                className="collapse-icon"
                onClick={() => toggleSection('prompt')}
                title={collapsedSections.prompt ? "Expand section" : "Collapse section"}
              >
                {collapsedSections.prompt ? <FaExpandArrowsAlt /> : <FaCompressArrowsAlt />}
              </button>
            </div>
          </div>
          {!collapsedSections.prompt && (
            <div className="section-content">
              <p>{prompt}</p>
            </div>
          )}
        </div>

        {/* Hyperparameters Section */}
        <div className={`section hyperparameters-section ${collapsedSections.hyperparameters ? 'collapsed' : ''}`}>
          <div className="section-header">
            <h2 className="section-title">Hyperparameters</h2>
            <div className="section-header-buttons">
              <button 
                className="collapse-icon"
                onClick={() => toggleSection('hyperparameters')}
                title={collapsedSections.hyperparameters ? "Expand section" : "Collapse section"}
              >
                {collapsedSections.hyperparameters ? <FaExpandArrowsAlt /> : <FaCompressArrowsAlt />}
              </button>
            </div>
          </div>
          {!collapsedSections.hyperparameters && (
            <div className="section-content hyperparameters-grid">
              <div className="param-item">
                <div className="param-label-container">
                  <span className="param-label">Temperature:</span>
                  <div 
                    className="info-icon"
                    onClick={() => setModalParam('temperature')}
                  >
                    ℹ️
                  </div>
                </div>
                <span className="param-value">{hyperparameters.temperature.value}</span>
              </div>
              <div className="param-item">
                <div className="param-label-container">
                  <span className="param-label">Tokens Used:</span>
                  <div 
                    className="info-icon"
                    onClick={() => setModalParam('tokensUsed')}
                  >
                    ℹ️
                  </div>
                </div>
                <span className="param-value">{hyperparameters.tokensUsed.value}</span>
              </div>
              <div className="param-item">
                <div className="param-label-container">
                  <span className="param-label">Top P:</span>
                  <div 
                    className="info-icon"
                    onClick={() => setModalParam('topP')}
                  >
                    ℹ️
                  </div>
                </div>
                <span className="param-value">{hyperparameters.topP.value}</span>
              </div>
              <div className="param-item">
                <div className="param-label-container">
                  <span className="param-label">Frequency Penalty:</span>
                  <div 
                    className="info-icon"
                    onClick={() => setModalParam('frequencyPenalty')}
                  >
                    ℹ️
                  </div>
                </div>
                <span className="param-value">{hyperparameters.frequencyPenalty.value}</span>
              </div>
              <div className="param-item">
                <div className="param-label-container">
                  <span className="param-label">Presence Penalty:</span>
                  <div 
                    className="info-icon"
                    onClick={() => setModalParam('presencePenalty')}
                  >
                    ℹ️
                  </div>
                </div>
                <span className="param-value">{hyperparameters.presencePenalty.value}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel */}
      <div className="right-panel">
        {/* Image Zoom Button */}
        <button 
          className="image-zoom-button"
          onClick={() => setShowImageModal(true)}
          title="View images in full size"
        >
          ⛶
        </button>

        <div className="images-container">
          <div className="image-item">
            <img 
              src={image1Path} 
              alt="Heatmap 1"
              className="heatmap-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="placeholder-image" style={{display: 'none'}}>
              <svg width="200" height="150" viewBox="0 0 200 150">
                <rect width="200" height="150" fill="#e0e0e0"/>
                <text x="50%" y="50%" textAnchor="middle" dy=".3em" fill="#999" fontSize="14">
                  Image 1 Not Found
                </text>
              </svg>
            </div>
          </div>
          <div className="image-item">
            <img 
              src={image2Path} 
              alt="Heatmap 2"
              className="heatmap-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="placeholder-image" style={{display: 'none'}}>
              <svg width="200" height="150" viewBox="0 0 200 150">
                <rect width="200" height="150" fill="#e0e0e0"/>
                <text x="50%" y="50%" textAnchor="middle" dy=".3em" fill="#999" fontSize="14">
                  Image 2 Not Found
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
      </div>
      ) : (
        // Annotator Mode
        <div className="annotator-mode-layout">
          {/* Question Section */}
          <div className="annotator-question">
            <h3>Question:</h3>
            <p>{prompt}</p>
          </div>

          {/* Images Section */}
          <div className="annotator-images">
            <div className="annotator-image-item">
              <h4>{ranker1Id}</h4>
              <img src={image1Path} alt={ranker1Id} />
            </div>
            <div className="annotator-image-item">
              <h4>{ranker2Id}</h4>
              <img src={image2Path} alt={ranker2Id} />
            </div>
          </div>

          {/* Selection Panel */}
          <div className="annotator-selection">
            <h4>Which scoring function is more stable?</h4>
            <div className="selection-buttons">
              <button
                className={`selection-button first ${humanSelection === 'first' ? 'selected' : ''}`}
                onClick={() => handleHumanSelection('first')}
              >
                {ranker1Id} {humanSelection === 'first' && '✓'}
              </button>
              <button
                className={`selection-button second ${humanSelection === 'second' ? 'selected' : ''}`}
                onClick={() => handleHumanSelection('second')}
              >
                {ranker2Id} {humanSelection === 'second' && '✓'}
              </button>
              <button
                className={`selection-button unclear ${humanSelection === 'unclear' ? 'selected' : ''}`}
                onClick={() => handleHumanSelection('unclear')}
              >
                Unclear / Equal {humanSelection === 'unclear' && '✓'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
